/* eslint-disable no-unused-vars */
import React, {
    useEffect, useState, useMemo
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    cn
} from 'shared/lib/utils';
import {
    Database, FileText, Image as ImageIcon, FileSpreadsheet, FileCode,
    Search, Loader2, AlertCircle, ChevronRight, Paperclip
} from 'lucide-react';
import {
    redisCollectionService
} from 'shared/api';
import {
    fileManagerService
} from 'shared/api/services/BEX/fileManager.service';

/**
 * KbFilePicker — pick a file from a Knowledge Base collection.
 *
 * Two-pane Dialog:
 *   - Left:  collections list (filtered by search)
 *   - Right: files inside the selected collection (with presigned URLs)
 *
 * On confirm, returns:
 *   {
 *     collection_id: string,
 *     file_name:     string,
 *     file_key:      string,
 *     url:           string (presigned GET URL — fetch contents or hand to vision model),
 *     content_type?: string,
 *     size?:         number
 *   }
 *
 * No new endpoints (UF-2). Composes existing redisCollectionService +
 * fileManagerService.listFilesWithPresignedUrl.
 */
export default function KbFilePicker({
    open, onOpenChange, onPick, title = 'Attach a file from Knowledge Base'
}) {
    const {
        oragID, subOragID
    } = useParams();
    const [collections, setCollections] = useState([]);
    const [collectionsErr, setCollectionsErr] = useState(null);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [activeCollection, setActiveCollection] = useState(null);
    const [files, setFiles] = useState([]);
    const [filesErr, setFilesErr] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);

    // Reset everything when the dialog re-opens.
    useEffect(() => {
        if (!open) return;
        setActiveCollection(null);
        setFiles([]);
        setSelected(null);
        setSearch('');
    }, [open]);

    // Fetch collections.
    useEffect(() => {
        if (!open || !oragID || !subOragID) return;
        let cancelled = false;
        setLoadingCollections(true);
        setCollectionsErr(null);
        redisCollectionService.listCollections(oragID, subOragID)
            .then((res) => {
                if (cancelled) return;
                const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
                setCollections(arr.filter((c) => c && getCollectionId(c)));
            })
            .catch((err) => {
                if (cancelled) return;
                setCollectionsErr(err?.response?.data?.message || err?.message || 'Could not load collections.');
            })
            .finally(() => { if (!cancelled) setLoadingCollections(false); });
        return () => { cancelled = true; };
    }, [open, oragID, subOragID]);

    // Fetch files when a collection is selected. Bucket id format mirrors
    // KBGlobalContext.getBucketId(): `coll-{subOragID}-{collectionId}`.
    useEffect(() => {
        if (!activeCollection || !oragID || !subOragID) return;
        let cancelled = false;
        const collectionId = getCollectionId(activeCollection);
        if (!collectionId) return undefined;
        const bucketId = `coll-${subOragID}-${collectionId}`;
        setLoadingFiles(true);
        setFilesErr(null);
        setFiles([]);
        setSelected(null);
        // paginated + delimiter:'/' matches KBGlobalContext.refresh() so we
        // get the same response shape ({ documents, CommonPrefixes, ... }).
        fileManagerService.listFilesWithPresignedUrl(oragID, bucketId, null, true, null, '/')
            .then((res) => {
                if (cancelled) return;
                const data = res?.data || res;
                // Standard KB response shape — `documents[]` is the list of files.
                const docs = Array.isArray(data?.documents) ? data.documents : [];
                // Filter out folder placeholder rows + system entries.
                const arr = docs
                    .filter((d) => d && d.Key && !/\/\.keep$/.test(d.Key))
                    .map((d) => ({
                        key: d.Key,
                        name: (d.Key || '').split('/').pop() || 'file',
                        url: d.url || d.preSignedUrl || d.presigned_url,
                        size: d.Size || d.size,
                        content_type: d.ContentType || d.content_type,
                        last_modified: d.LastModified || d.last_modified
                    }))
                    .filter((f) => f.name && f.name !== '.keep');
                setFiles(arr);
            })
            .catch((err) => {
                if (cancelled) return;
                setFilesErr(err?.response?.data?.message || err?.message || 'Could not load files for this collection.');
            })
            .finally(() => { if (!cancelled) setLoadingFiles(false); });
        return () => { cancelled = true; };
    }, [activeCollection, oragID, subOragID]);

    const filteredCollections = useMemo(() => {
        if (!search.trim()) return collections;
        const q = search.toLowerCase();
        return collections.filter((c) => {
            const label = getCollectionDisplayName(c).toLowerCase();
            const id = getCollectionId(c).toLowerCase();
            return label.includes(q) || id.includes(q);
        });
    }, [collections, search]);

    function confirm() {
        if (!selected) return;
        const fname = selected.name || selected.file_name || (selected.key || '').split('/').pop() || 'file';
        onPick({
            collection_id: getCollectionId(activeCollection),
            file_name: fname,
            file_key: selected.key || selected.file_key || '',
            url: selected.url || selected.presigned_url || selected.preSignedUrl || '',
            content_type: selected.content_type || selected.contentType,
            size: selected.size
        });
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col gap-0 p-0">
                <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Paperclip className="h-4 w-4" /> {title}
                    </DialogTitle>
                    <DialogDescription className="text-[12px]">
                        Pick a Knowledge Base collection on the left, then a file on the right.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid flex-1 min-h-0 grid-cols-[260px_1fr] border-t">
                    {/* Collections */}
                    <div className="flex flex-col min-h-0 border-r bg-muted/20">
                        <div className="flex-shrink-0 border-b bg-card p-2">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search collections…"
                                    className="h-7 pl-7 text-[12px]"
                                />
                            </div>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-1.5">
                            {loadingCollections && (
                                <div className="flex items-center gap-2 px-2 py-2 text-[11.5px] text-muted-foreground">
                                    <Loader2 className="h-3 w-3 animate-spin" /> loading collections…
                                </div>
                            )}
                            {collectionsErr && (
                                <ErrorRow text={collectionsErr} />
                            )}
                            {!loadingCollections && !collectionsErr && filteredCollections.length === 0 && (
                                <div className="rounded-md border border-dashed bg-card px-3 py-2 text-[11.5px] text-muted-foreground">
                                    {search ? 'No matches.' : 'No collections in this sub-org. Create one in Knowledge.'}
                                </div>
                            )}
                            {filteredCollections.map((c) => {
                                const id = getCollectionId(c);
                                const name = getCollectionName(c);
                                const title = name || id;
                                const active = activeCollection
                                    && getCollectionId(activeCollection) === id;
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setActiveCollection(c)}
                                        className={cn(
                                            'mb-0.5 flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left transition-colors',
                                            active
                                                ? 'border-primary bg-primary/5'
                                                : 'border-transparent hover:border-foreground/20 hover:bg-card'
                                        )}
                                    >
                                        <Database className="h-3.5 w-3.5 flex-shrink-0 text-rose-500" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-[12px] font-medium">{title}</div>
                                            {name && id && name !== id && (
                                                <div className="truncate font-mono text-[10px] text-muted-foreground">{id}</div>
                                            )}
                                        </div>
                                        {active && <ChevronRight className="h-3 w-3 text-primary" />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Files */}
                    <div className="flex flex-col min-h-0 bg-card">
                        <div className="flex-shrink-0 border-b px-3 py-2 text-[11.5px] uppercase tracking-wider text-muted-foreground">
                            {activeCollection
                                ? (
                                    <span>
                                        Files in{' '}
                                        <span className="font-medium text-foreground normal-case tracking-normal">
                                            {getCollectionDisplayName(activeCollection)}
                                        </span>
                                    </span>
                                )
                                : <span>Pick a collection to see its files →</span>}
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
                            {!activeCollection && (
                                <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
                                    No collection selected.
                                </div>
                            )}
                            {activeCollection && loadingFiles && (
                                <div className="flex items-center gap-2 px-2 py-2 text-[11.5px] text-muted-foreground">
                                    <Loader2 className="h-3 w-3 animate-spin" /> loading files…
                                </div>
                            )}
                            {activeCollection && filesErr && <ErrorRow text={filesErr} />}
                            {activeCollection && !loadingFiles && !filesErr && files.length === 0 && (
                                <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11.5px] text-muted-foreground">
                                    No files in this collection yet. Upload some in the Knowledge tab.
                                </div>
                            )}
                            {files.map((f) => {
                                const fkey = f.key || f.file_key || f.name || JSON.stringify(f);
                                const fname = f.name || f.file_name || (f.key || '').split('/').pop() || 'file';
                                const Icon = iconForFile(fname);
                                const sel = selected && (selected.key || selected.file_key || selected.name) === (f.key || f.file_key || f.name);
                                return (
                                    <button
                                        key={fkey}
                                        type="button"
                                        onClick={() => setSelected(f)}
                                        className={cn(
                                            'flex w-full items-center gap-2.5 rounded-md border bg-card px-2.5 py-1.5 text-left transition-colors',
                                            sel ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/20'
                                        )}
                                    >
                                        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-[12.5px]">{fname}</div>
                                            {f.size != null && (
                                                <div className="truncate font-mono text-[10px] text-muted-foreground">
                                                    {humanSize(f.size)}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex flex-shrink-0 items-center justify-between border-t bg-muted/20 px-4 py-3">
                    <div className="text-[11.5px] text-muted-foreground">
                        {selected
                            ? <>Selected: <span className="font-mono">{selected.name || selected.file_name || (selected.key || '').split('/').pop()}</span></>
                            : 'Pick a file to attach.'}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button size="sm" onClick={confirm} disabled={!selected}>
                            <Paperclip className="mr-1 h-3 w-3" /> Attach
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function getCollectionId(c) {
    if (!c || typeof c !== 'object') return '';
    return String(c._id || c.collection_id || c.collectionId || '').trim();
}

/** Human-readable label — KB collections use `name` (not `collection_name`). */
function getCollectionName(c) {
    if (!c || typeof c !== 'object') return '';
    return String(c.name || c.collection_name || c.title || '').trim();
}

function getCollectionDisplayName(c) {
    const name = getCollectionName(c);
    const id = getCollectionId(c);
    return name || id;
}

function iconForFile(name) {
    const lower = (name || '').toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return ImageIcon;
    if (/\.(csv|xlsx?|tsv)$/.test(lower)) return FileSpreadsheet;
    if (/\.(json|yml|yaml|md|js|ts|py|sh)$/.test(lower)) return FileCode;
    return FileText;
}

function humanSize(b) {
    if (b == null || isNaN(b)) return '';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let v = Number(b);
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function ErrorRow({
    text
}) {
    return (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[11.5px] text-destructive">
            <AlertCircle className="mt-0.5 h-3 w-3 flex-shrink-0" />
            <span>{text}</span>
        </div>
    );
}
